import React, { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import Cropper from "react-easy-crop";

import Modal from "../Modal";
import Button from "../Button";
import Avatar from "../Avatar";
import Icon from "../Icon";
import { getCroppedImg } from "../../helpers/cropImage";

import "react-easy-crop/react-easy-crop.css";
import "./styles.scss";

// Teto do arquivo bruto escolhido, ANTES do recorte. O crop reencoda a imagem
// num quadrado pequeno, então o que vai ao backend é bem menor que isto — este
// limite só evita carregar um arquivo gigante na memória do navegador.
const MAX_INPUT_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

// Saída do recorte: quadrado 1:1 reencodado em JPEG. Mantém o Data URL enviado
// ao backend leve, evitando o erro de payload grande no servidor (post_max_size).
const OUTPUT_SIZE = 512;
const OUTPUT_TYPE = 'image/jpeg';
const OUTPUT_QUALITY = 0.9;

/**
 * Upload de foto de perfil com recorte 1:1. Encapsula o input, a validação de
 * tipo/tamanho e o modal de recorte, devolvendo via `onChange` a imagem já
 * recortada e reencodada como Data URL (base64), pronta para enviar ao backend.
 *
 * Compartilhado entre "Meus dados" (ModalMyProfile) e a edição de usuário
 * (pages/User) para não duplicar a lógica de upload.
 */
export const AvatarUpload = ({ name, photo, size = 72, onChange }) => {
    const inputRef = useRef(null);

    const [src, setSrc] = useState(null); // Data URL do arquivo original em recorte
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [processing, setProcessing] = useState(false);

    const resetInput = () => {
        // Permite reescolher o mesmo arquivo (o onChange do input não dispara
        // com o mesmo value).
        if (inputRef.current) inputRef.current.value = '';
    };

    const choosePhoto = e => {
        const file = e.target.files[0];
        if (!file) return;

        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.error('O formato do arquivo é inválido. Envie PNG, JPG, GIF ou WebP.');
            resetInput();
            return;
        }

        if (file.size > MAX_INPUT_SIZE) {
            toast.error('O arquivo enviado é muito grande. O tamanho máximo é 10MB.');
            resetInput();
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setSrc(reader.result);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
        };
        reader.onerror = () => toast.error('Não foi possível ler o arquivo.');
        reader.readAsDataURL(file);
        resetInput();
    };

    const onCropComplete = useCallback((_area, areaPixels) => {
        setCroppedAreaPixels(areaPixels);
    }, []);

    const closeCrop = () => {
        setSrc(null);
        setCroppedAreaPixels(null);
    };

    const confirmCrop = async () => {
        if (!croppedAreaPixels) return;

        setProcessing(true);
        try {
            const base64 = await getCroppedImg(src, croppedAreaPixels, {
                size: OUTPUT_SIZE,
                type: OUTPUT_TYPE,
                quality: OUTPUT_QUALITY,
            });
            onChange(base64);
            closeCrop();
        } catch (error) {
            console.error(error);
            toast.error('Não foi possível recortar a imagem.');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <>
            <div className="avatar-upload">
                <Avatar name={name} photo={photo} size={size} />
                <label className="change-photo">
                    <Icon name="upload-small" size={16} /> Alterar foto
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/png, image/jpeg, image/gif, image/webp"
                        onChange={choosePhoto}
                    />
                </label>
            </div>

            <Modal
                className="avatar-crop-modal"
                title="Recortar foto"
                show={src !== null}
                close={closeCrop}
                footer={
                    <>
                        <Button type="secondary" size="small" text="Cancelar" action={closeCrop} />
                        <Button
                            type="primary"
                            size="small"
                            text="Aplicar recorte"
                            svg="save-white"
                            action={confirmCrop}
                            loading={processing}
                        />
                    </>
                }
            >
                <div className="avatar-crop">
                    <div className="crop-area">
                        {src &&
                            <Cropper
                                image={src}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                restrictPosition={true}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        }
                    </div>
                    <div className="crop-zoom">
                        <Icon name="image" size={14} />
                        <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.01}
                            value={zoom}
                            onChange={e => setZoom(Number(e.target.value))}
                            aria-label="Zoom"
                        />
                        <Icon name="image" size={22} />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default AvatarUpload;
